import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Flower2, Sprout, Users, Sparkles, UserRound, ArrowRight, ArrowLeft } from "lucide-react";

const STEPS = [
  { icon: Flower2, title: "Welcome to Bouquet",
    body: "This is your bird's-eye view of what's growing — your intentions, your people, your moments. Nothing here is scored, ranked, or compared." },
  { icon: Sprout, title: "Name what's growing",
    body: "Start with an intention and one small next step. It stays private until you choose to share it." },
  { icon: Users, title: "Gather your pod",
    body: "Pods are small circles of people you trust. Share a next step and let them show up for you — conversation happens in your own group chats." },
  { icon: Sparkles, title: "Keep the moments",
    body: "Milestones, dinners, small wins — preserve them for yourself, your pod, or your community." },
  { icon: UserRound, title: "Last step: your profile",
    body: "A photo and a line about you help your people recognize you in pods and communities. Takes thirty seconds.", cta: true },
];

export default function Tour({ onDone }) {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  const finish = async (goProfile) => {
    try { await api.post("/profile/tour-seen"); } catch { /* best effort */ }
    onDone();
    if (goProfile) navigate("/profile");
  };

  const s = STEPS[step];
  const Icon = s.icon;
  const last = step === STEPS.length - 1;

  return (
    <div className="fixed inset-0 z-50 bg-background/70 backdrop-blur-sm flex items-center justify-center px-4" data-testid="tour-modal">
      <div className="w-full max-w-md bg-card border border-border rounded-2xl p-8 space-y-6 text-center">
        <div className="h-12 w-12 rounded-full bg-sage-pale mx-auto flex items-center justify-center">
          <Icon className="h-6 w-6 text-primary" />
        </div>
        <div className="space-y-2">
          <h3 className="font-display text-2xl font-medium" data-testid="tour-title">{s.title}</h3>
          <p className="text-sm text-muted-foreground leading-relaxed" data-testid="tour-body">{s.body}</p>
        </div>
        <div className="flex justify-center gap-1.5" data-testid="tour-dots">
          {STEPS.map((_, i) => (
            <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? "w-5 bg-primary" : "w-1.5 bg-border"}`} />
          ))}
        </div>
        <div className="flex gap-2">
          {step > 0 && (
            <button data-testid="tour-back" onClick={() => setStep(step - 1)}
              className="px-4 py-3 rounded-full border border-border text-sm font-semibold hover:bg-secondary transition-colors">
              <ArrowLeft className="h-4 w-4" />
            </button>
          )}
          {last ? (
            <button data-testid="tour-complete-profile" onClick={() => finish(true)}
              className="flex-1 bg-primary text-primary-foreground py-3 rounded-full text-sm font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity">
              Complete my profile <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button data-testid="tour-next" onClick={() => setStep(step + 1)}
              className="flex-1 bg-primary text-primary-foreground py-3 rounded-full text-sm font-semibold hover:opacity-90 transition-opacity">Next</button>
          )}
        </div>
        <button data-testid="tour-skip" onClick={() => finish(false)}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors">Skip the tour</button>
      </div>
    </div>
  );
}
